import { useState } from 'react';
import { DollarSign, Plus, AlertCircle, CheckCircle, XCircle } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../api/apiClient';

interface EnterpriseCode {
  id: number;
  code: string;
  description?: string;
  fixedPricePerLease?: number;
  maxLeasesCap?: number;
  isActive: boolean;
  validFrom?: string;
  validUntil?: string;
  maxUses?: number;
  currentUses: number;
  createdBy?: string;
  createdAt?: string;
}

function generateCode() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = 'ENT';
  for (let i = 0; i < 6; i++) code += chars[Math.floor(Math.random() * chars.length)];
  return code;
}

export default function AdminEnterpriseCodesPage() {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ code: generateCode(), description: '', maxUses: '' });
  const [formError, setFormError] = useState('');

  const { data: codes = [], isLoading, isError, error } = useQuery<EnterpriseCode[]>({
    queryKey: ['admin', 'enterprise-codes'],
    queryFn: () => api.get<EnterpriseCode[]>('/admin/agencies/enterprise-codes').then(r => r ?? []),
    staleTime: 30_000,
  });

  const createCode = useMutation({
    mutationFn: (body: { code: string; description: string; maxUses?: number }) =>
      api.post('/admin/agencies/enterprise-codes', body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'enterprise-codes'] });
      setShowForm(false);
      setForm({ code: generateCode(), description: '', maxUses: '' });
      setFormError('');
    },
    onError: (err: any) => setFormError(err?.message || 'Failed to create code'),
  });

  const deactivateCode = useMutation({
    mutationFn: (id: number) => api.delete(`/admin/agencies/enterprise-codes/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'enterprise-codes'] }),
  });

  const handleCreate = () => {
    if (!form.code.trim()) { setFormError('Code is required'); return; }
    createCode.mutate({
      code: form.code.toUpperCase(),
      description: form.description,
      maxUses: form.maxUses ? parseInt(form.maxUses) : undefined,
    });
  };

  const formatDate = (d?: string) =>
    d ? new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <PageHeader
        title="Enterprise Codes"
        subtitle="Manage discount and enterprise access codes"
        icon={<DollarSign className="w-8 h-8" />}
      >
        <button
          onClick={() => setShowForm(v => !v)}
          className="flex items-center gap-2 bg-secondary text-white px-4 py-2 rounded-lg hover:bg-secondary/90 text-sm font-medium"
        >
          <Plus className="w-4 h-4" />
          Create Code
        </button>
      </PageHeader>

      {isError && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="text-red-800">{(error as Error)?.message || 'Failed to load codes'}</div>
        </div>
      )}

      {/* Create form */}
      {showForm && (
        <div className="bg-white rounded-lg shadow p-6 mb-6 border border-secondary/30">
          <h3 className="font-semibold text-gray-900 mb-4">New Enterprise Code</h3>
          {formError && (
            <div className="mb-3 text-sm text-red-600 bg-red-50 rounded-lg p-3">{formError}</div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Code *</label>
              <div className="flex gap-2">
                <input
                  value={form.code}
                  onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))}
                  className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 font-mono"
                />
                <button
                  onClick={() => setForm(f => ({ ...f, code: generateCode() }))}
                  className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm"
                >↻</button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <input
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Optional description"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Max Uses</label>
              <input
                type="number"
                value={form.maxUses}
                onChange={e => setForm(f => ({ ...f, maxUses: e.target.value }))}
                placeholder="Unlimited"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
            </div>
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleCreate}
              disabled={createCode.isPending}
              className="bg-secondary text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-secondary/90 disabled:opacity-50"
            >
              {createCode.isPending ? 'Creating…' : 'Create Code'}
            </button>
            <button
              onClick={() => { setShowForm(false); setFormError(''); }}
              className="px-4 py-2 rounded-lg text-sm text-gray-600 hover:bg-gray-100"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <p className="text-sm text-gray-500">{codes.length} code{codes.length !== 1 ? 's' : ''}</p>
          </div>
          {codes.length === 0 ? (
            <div className="p-12 text-center text-gray-500">No enterprise codes yet. Create one above.</div>
          ) : (
            <div className="divide-y divide-gray-200">
              {codes.map(code => (
                <div key={code.id} className="p-4 sm:p-6 flex items-center justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1">
                      <span className="font-mono font-bold text-primary text-lg">{code.code}</span>
                      {code.isActive
                        ? <span className="px-2 py-0.5 text-xs bg-green-100 text-green-800 rounded-full flex items-center gap-1"><CheckCircle className="w-3 h-3" />Active</span>
                        : <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full flex items-center gap-1"><XCircle className="w-3 h-3" />Inactive</span>
                      }
                    </div>
                    {code.description && <p className="text-sm text-gray-500">{code.description}</p>}
                    <p className="text-xs text-gray-400 mt-1">
                      Uses: {code.currentUses}{code.maxUses ? ` / ${code.maxUses}` : ' / ∞'}
                      {code.validUntil ? ` · Expires: ${formatDate(code.validUntil)}` : ''}
                      {code.createdAt ? ` · Created: ${formatDate(code.createdAt)}` : ''}
                    </p>
                  </div>
                  {code.isActive && (
                    <button
                      onClick={() => deactivateCode.mutate(code.id)}
                      className="text-xs text-red-600 hover:text-red-800 border border-red-200 px-3 py-1.5 rounded-lg hover:bg-red-50"
                    >
                      Deactivate
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
